import { calculateOrder, type CalcConfig, formatRupiah } from '@safepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import type { ProductDto } from './CatalogPages';
import { useInvalidate } from './CatalogPages';

interface Outlet {
  id: string;
  name: string;
}

export function OutletPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data = [] } = useQuery({
    queryKey: ['outlets'],
    queryFn: () => api.get<Outlet[]>('/outlets'),
  });
  useEffect(() => {
    if (!value && data[0]) onChange(data[0].id);
  }, [data, value, onChange]);
  return (
    <div className="w-64">
      <SelectField label="Outlet" value={value} onChange={(e) => onChange(e.target.value)}>
        {data.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </SelectField>
    </div>
  );
}

// ---------- Harga & ketersediaan per outlet ----------
interface Override {
  variantId: string;
  priceOverride: number | null;
  available: boolean;
}

export function OutletPricesPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const [outletId, setOutletId] = useState('');
  const products = useQuery({
    queryKey: ['products', 'all-for-outlet'],
    queryFn: () => api.get<{ data: ProductDto[] }>('/products?limit=200&active=true'),
  });
  const overrides = useQuery({
    queryKey: ['outlet-products', outletId],
    enabled: !!outletId,
    queryFn: () => api.get<Override[]>(`/outlet-products?outletId=${outletId}`),
  });
  const [draft, setDraft] = useState<Record<string, { price: string; available: boolean }>>({});
  const map = new Map((overrides.data ?? []).map((o) => [o.variantId, o]));

  const save = useSubmit(async (variantId: string) => {
    const d = draft[variantId]!;
    await api.put('/outlet-products', {
      outletId,
      variantId,
      priceOverride: d.price === '' ? null : Number(d.price),
      available: d.available,
    });
    inv('outlet-products');
    setDraft(({ [variantId]: _, ...rest }) => rest);
  });
  const reset = useSubmit(async (variantId: string) => {
    await api.del(`/outlet-products?outletId=${outletId}&variantId=${variantId}`);
    inv('outlet-products');
    setDraft(({ [variantId]: _, ...rest }) => rest);
  });
  const rows = (products.data?.data ?? []).flatMap((p) => p.variants.map((v) => ({ p, v })));
  return (
    <>
      <PageHeader
        title="Harga & Ketersediaan Outlet"
        subtitle="Atur harga khusus atau sembunyikan produk di outlet tertentu"
      />
      {(save.error || reset.error) && <Alert>{save.error ?? reset.error}</Alert>}
      <Card actions={<OutletPicker value={outletId} onChange={setOutletId} />}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Produk</th>
                <th className="px-3 py-2">Harga dasar</th>
                <th className="px-3 py-2">Harga outlet</th>
                <th className="px-3 py-2">Tersedia</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                    Belum ada produk aktif.
                  </td>
                </tr>
              )}
              {rows.map(({ p, v }) => {
                const o = map.get(v.id);
                const d = draft[v.id] ?? {
                  price: o?.priceOverride == null ? '' : String(o.priceOverride),
                  available: o?.available ?? true,
                };
                const dirty = !!draft[v.id];
                return (
                  <tr key={v.id}>
                    <td className="px-3 py-2">
                      <span className="font-medium">{p.name}</span>
                      {p.type === 'variant' && <span className="text-slate-500"> — {v.name}</span>}
                    </td>
                    <td className="px-3 py-2">{formatRupiah(v.price)}</td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`Harga outlet ${p.name} ${v.name}`}
                        inputMode="numeric"
                        placeholder="Sama"
                        disabled={!can('catalog:write')}
                        className="w-32 rounded-lg border border-slate-300 px-2 py-1"
                        value={d.price}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            [v.id]: { ...d, price: e.target.value.replace(/\D/g, '') },
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Tersedia ${p.name} ${v.name}`}
                        disabled={!can('catalog:write')}
                        checked={d.available}
                        onChange={(e) =>
                          setDraft({ ...draft, [v.id]: { ...d, available: e.target.checked } })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {can('catalog:write') && (
                        <div className="flex justify-end gap-2">
                          {dirty && <Button onClick={() => void save.run(v.id)}>Simpan</Button>}
                          {o && !dirty && (
                            <Button variant="ghost" onClick={() => void reset.run(v.id)}>
                              Reset
                            </Button>
                          )}
                          {o && <Badge tone="info">Khusus</Badge>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {products.data && products.data.data.length >= 200 && (
          <p className="mt-3 text-xs text-slate-500">Menampilkan 200 produk pertama.</p>
        )}
      </Card>
    </>
  );
}

// ---------- Pajak, service charge & struk ----------
interface Setting {
  outletId: string;
  taxBp: number;
  taxInclusive: boolean;
  serviceBp: number;
  serviceInclusive: boolean;
  taxOnService: boolean;
  rounding: 'none' | '100' | '500' | '1000';
  businessName: string | null;
  npwp: string | null;
  receiptHeader: string | null;
  receiptFooter: string | null;
  paperWidth: 58 | 80;
  blockZeroStock: boolean;
}
const pct = (bp: number) => String(bp / 100);
const toBp = (s: string) =>
  Math.max(0, Math.min(10_000, Math.round(Number(s.replace(',', '.')) * 100) || 0));

export function OutletSettingsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const [outletId, setOutletId] = useState('');
  const query = useQuery({
    queryKey: ['outlet-settings', outletId],
    enabled: !!outletId,
    queryFn: () => api.get<Setting>(`/outlets/${outletId}/settings`),
  });
  const [f, setF] = useState<Setting | null>(null);
  const [tax, setTax] = useState('11');
  const [service, setService] = useState('0');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (query.data) {
      setF(query.data);
      setTax(pct(query.data.taxBp));
      setService(pct(query.data.serviceBp));
      setSaved(false);
    }
  }, [query.data]);
  const save = useSubmit(async () => {
    const body = {
      ...f!,
      taxBp: toBp(tax),
      serviceBp: toBp(service),
      businessName: f!.businessName || null,
      npwp: f!.npwp || null,
      receiptHeader: f!.receiptHeader || null,
      receiptFooter: f!.receiptFooter || null,
    };
    const { outletId: _o, ...payload } = body;
    await api.put(`/outlets/${outletId}/settings`, payload);
    inv('outlet-settings');
    setSaved(true);
  });
  const writable = can('settings:write');
  // Pratinjau memakai mesin hitung yang SAMA dengan kasir & server
  const cfg: CalcConfig | null = f && {
    taxBp: toBp(tax),
    taxInclusive: f.taxInclusive,
    serviceBp: toBp(service),
    serviceInclusive: f.serviceInclusive,
    taxOnService: f.taxOnService,
    rounding: f.rounding,
  };
  const demo =
    cfg &&
    calculateOrder(
      [
        { unitPrice: 25_000, qty: 2 },
        { unitPrice: 22_000, qty: 1 },
      ],
      cfg,
    );
  return (
    <>
      <PageHeader
        title="Pajak & Struk"
        subtitle="PPN, service charge, pembulatan, dan tampilan struk per outlet"
      />
      <Card actions={<OutletPicker value={outletId} onChange={setOutletId} />}>
        {!f ? (
          'Memuat…'
        ) : (
          <form
            className="grid gap-6 lg:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            <div className="space-y-4">
              {save.error && <Alert>{save.error}</Alert>}
              {saved && <Alert tone="success">Pengaturan disimpan.</Alert>}
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="PPN (%)"
                  inputMode="decimal"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  disabled={!writable}
                  hint="0 = tanpa PPN"
                />
                <TextField
                  label="Service charge (%)"
                  inputMode="decimal"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  disabled={!writable}
                />
              </div>
              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!writable}
                    checked={f.taxInclusive}
                    onChange={(e) => setF({ ...f, taxInclusive: e.target.checked })}
                  />{' '}
                  Harga sudah termasuk PPN
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!writable}
                    checked={f.serviceInclusive}
                    onChange={(e) => setF({ ...f, serviceInclusive: e.target.checked })}
                  />{' '}
                  Harga sudah termasuk service charge
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!writable}
                    checked={f.taxOnService}
                    onChange={(e) => setF({ ...f, taxOnService: e.target.checked })}
                  />{' '}
                  PPN dikenakan juga atas service charge
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!writable}
                    checked={f.blockZeroStock}
                    onChange={(e) => setF({ ...f, blockZeroStock: e.target.checked })}
                  />{' '}
                  Larang penjualan bila stok barang terlacak habis
                </label>
              </div>
              <SelectField
                label="Pembulatan total"
                value={f.rounding}
                disabled={!writable}
                onChange={(e) => setF({ ...f, rounding: e.target.value as Setting['rounding'] })}
              >
                <option value="none">Tanpa pembulatan</option>
                <option value="100">Ke Rp 100 terdekat</option>
                <option value="500">Ke Rp 500 terdekat</option>
                <option value="1000">Ke Rp 1.000 terdekat</option>
              </SelectField>
              <TextField
                label="Nama usaha di struk"
                value={f.businessName ?? ''}
                disabled={!writable}
                onChange={(e) => setF({ ...f, businessName: e.target.value })}
              />
              <TextField
                label="NPWP"
                value={f.npwp ?? ''}
                disabled={!writable}
                onChange={(e) => setF({ ...f, npwp: e.target.value })}
              />
              <TextAreaField
                label="Header struk"
                rows={2}
                value={f.receiptHeader ?? ''}
                disabled={!writable}
                onChange={(e) => setF({ ...f, receiptHeader: e.target.value })}
              />
              <TextAreaField
                label="Footer struk"
                rows={2}
                value={f.receiptFooter ?? ''}
                disabled={!writable}
                onChange={(e) => setF({ ...f, receiptFooter: e.target.value })}
              />
              <SelectField
                label="Lebar kertas"
                value={String(f.paperWidth)}
                disabled={!writable}
                onChange={(e) => setF({ ...f, paperWidth: Number(e.target.value) as 58 | 80 })}
              >
                <option value="58">58 mm</option>
                <option value="80">80 mm</option>
              </SelectField>
              {writable && (
                <Button type="submit" disabled={save.busy}>
                  Simpan pengaturan
                </Button>
              )}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-medium text-slate-700">
                Pratinjau struk (contoh 2× Kopi + 1× Roti)
              </h3>
              <div
                className={`mx-auto rounded border border-dashed border-slate-300 bg-surface p-4 font-mono text-xs ${f.paperWidth === 58 ? 'max-w-[220px]' : 'max-w-[300px]'}`}
              >
                <div className="text-center">
                  <div className="font-bold">{f.businessName || 'NAMA USAHA'}</div>
                  {f.npwp && <div>NPWP {f.npwp}</div>}
                  {f.receiptHeader && <div className="whitespace-pre-wrap">{f.receiptHeader}</div>}
                </div>
                <hr className="my-2 border-slate-300" />
                {demo && (
                  <dl className="space-y-0.5">
                    <Line k="2x Kopi Susu" v={formatRupiah(50_000)} />
                    <Line k="1x Roti Bakar" v={formatRupiah(22_000)} />
                    <hr className="my-1 border-slate-300" />
                    <Line k="Subtotal" v={formatRupiah(demo.subtotal)} />
                    {demo.service > 0 && (
                      <Line k={`Service ${service}%`} v={formatRupiah(demo.service)} />
                    )}
                    {demo.tax > 0 && (
                      <Line
                        k={`PPN ${tax}%${f.taxInclusive ? ' (incl)' : ''}`}
                        v={formatRupiah(demo.tax)}
                      />
                    )}
                    {demo.rounding !== 0 && <Line k="Pembulatan" v={formatRupiah(demo.rounding)} />}
                    <Line k="TOTAL" v={formatRupiah(demo.grandTotal)} bold />
                  </dl>
                )}
                {f.receiptFooter && (
                  <div className="mt-2 whitespace-pre-wrap text-center">{f.receiptFooter}</div>
                )}
              </div>
            </div>
          </form>
        )}
      </Card>
    </>
  );
}
function Line({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-bold' : ''}`}>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}
